import {
  Bell,
  CalendarClock,
  ChevronDown,
  LogOut,
  Menu,
  Search,
  Settings,
  Users,
  UserRound,
} from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { logout } from "@/features/auth/api";
import { useAuthStore } from "@/features/auth/store/auth-store";
import { useAnnouncements } from "@/features/admin/hooks/use-announcements";
import { useUIStore } from "@/store/ui-store";

function initials(name: string) {
  return (
    name
      .split(" ")
      .map((part) => part[0])
      .slice(0, 2)
      .join("")
      .toUpperCase() || "?"
  );
}

/** Bell dropdown fed by the firm announcements poll (broadcast + upcoming events). */
function NotificationsMenu() {
  const { announcements } = useAnnouncements();
  const broadcast = announcements?.broadcast ?? null;
  const events = announcements?.events ?? [];
  const count = (broadcast ? 1 : 0) + events.length;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label={`Notifications${count ? ` (${count})` : ""}`}
          className="border-border bg-card text-foreground hover:bg-muted relative flex size-11 items-center justify-center rounded-md border transition-colors"
        >
          <Bell className="size-[18px]" />
          {count > 0 && (
            <span className="bg-primary border-card absolute top-2.5 right-3 size-[7px] rounded-full border-2" />
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80">
        <DropdownMenuLabel>Notifications</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {count === 0 ? (
          <div className="text-muted-foreground px-2 py-6 text-center text-sm">
            You're all caught up.
          </div>
        ) : (
          <div className="max-h-96 overflow-y-auto">
            {broadcast && (
              <div className="px-2 py-2">
                <p className="text-foreground text-sm font-medium">Announcement</p>
                <p className="text-muted-foreground text-sm">{broadcast.message}</p>
              </div>
            )}
            {events.map((event) => (
              <DropdownMenuItem key={event.id} asChild className="items-start gap-2 py-2">
                <Link to="/dashboard">
                  <CalendarClock className="text-muted-foreground mt-0.5 size-4 shrink-0" />
                  <span className="min-w-0">
                    <span className="text-foreground block truncate font-medium">
                      {event.title}
                    </span>
                    {(event.eventDate || event.eventTime) && (
                      <span className="text-muted-foreground block text-xs">
                        {[event.eventDate, event.eventTime].filter(Boolean).join(" · ")}
                      </span>
                    )}
                  </span>
                </Link>
              </DropdownMenuItem>
            ))}
          </div>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/** Signed-in user name + avatar with a profile/settings/sign-out menu. */
function UserMenu() {
  const user = useAuthStore((s) => s.user);
  const navigate = useNavigate();
  const name = user?.name ?? "Account";
  const firstName = name.split(" ")[0] ?? name;

  async function handleSignOut() {
    await logout();
    navigate("/login");
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label="Account menu"
          className="border-border bg-card text-foreground hover:bg-muted flex h-11 items-center gap-2.5 rounded-md border py-1 pr-3.5 pl-1.5 text-sm font-medium transition-colors"
        >
          <Avatar className="size-8 rounded-[10px]">
            <AvatarFallback className="bg-accent text-accent-foreground font-heading rounded-[10px] text-xs font-bold">
              {initials(name)}
            </AvatarFallback>
          </Avatar>
          <span className="hidden max-w-[120px] truncate lg:block">{firstName}</span>
          <ChevronDown className="size-3.5" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel className="flex flex-col">
          <span className="truncate font-semibold">{name}</span>
          {user?.email && (
            <span className="text-muted-foreground truncate text-xs font-normal">{user.email}</span>
          )}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild>
          <Link to="/profile">
            <UserRound /> My Profile
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem asChild>
          <Link to="/team">
            <Users /> Team
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem asChild>
          <Link to="/settings">
            <Settings /> Settings
          </Link>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem variant="destructive" onSelect={() => void handleSignOut()}>
          <LogOut /> Sign out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/**
 * Content top bar (Calm Studio mockups): a search field + bell + account chip,
 * rendered at the top of the page content (not a fixed band) so it scrolls on
 * the gradient background — matching the design on every page.
 */
export function AppTopBar() {
  const toggleMobileNav = useUIStore((s) => s.toggleMobileNav);
  return (
    <header className="relative flex flex-wrap items-center gap-4">
      <button
        type="button"
        onClick={toggleMobileNav}
        aria-label="Open navigation"
        className="border-border bg-card text-foreground hover:bg-muted flex size-11 shrink-0 items-center justify-center rounded-md border transition-colors lg:hidden"
      >
        <Menu className="size-[18px]" />
      </button>
      <Link
        to="/search"
        className="border-border bg-card text-muted-foreground hover:border-ring flex h-11 max-w-[460px] flex-1 items-center gap-2.5 rounded-md border px-4 text-sm transition-colors"
      >
        <Search className="size-[18px]" />
        <span className="flex-1 truncate">Search clients, cases, notices…</span>
        <kbd className="border-border text-muted-foreground bg-background rounded-md border px-1.5 py-0.5 font-sans text-[11px]">
          ⌘K
        </kbd>
      </Link>
      <div className="grow" />
      <div className="flex items-center gap-2.5">
        <NotificationsMenu />
        <UserMenu />
      </div>
    </header>
  );
}
